import {
  ExpressionSpecification,
  GeoJSONSource,
  type Map as MaplibreMap,
  SourceSpecification
} from "maplibre-gl";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  MultiPolygon,
  Point,
  Polygon
} from "geojson";

import { COLOR_KEYS, COLORS } from "@/constants/colors";
import { DEFAULT_STEP } from "@/constants/dataset";
import { Dataset, DatasetStyle } from "@/types/dataset";
import { FeatureDataConfig } from "@/types/map";

import pointOnFeature from "@turf/point-on-feature";

import { generateColorRangesUniqueStops } from "../common";
import { addLabelLayer } from "./label";
import { addRankLabelLayer } from "./rank";

export const addCentroidSource = (
  map: MaplibreMap | null,
  centroidSourceId: string,
  polygonData: FeatureCollection<Geometry>
): void => {
  if (!map) return;

  const parsedPolygonData: FeatureCollection<Geometry> = {
    ...polygonData,
    features: polygonData.features.map((feature, index) => ({
      ...feature,
      properties: {
        ...feature.properties,
        unique_id: feature.properties?.unique_id || index
      }
    }))
  };

  const polygonFeatures = parsedPolygonData.features.filter(
    (f): f is Feature<Polygon | MultiPolygon> =>
      f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon"
  );

  const centroidFeatures: Feature<Point>[] = polygonFeatures.map((feature) => {
    const centroid = pointOnFeature(feature);
    centroid.properties = { ...feature.properties };
    return centroid;
  });

  const centroidCollection: FeatureCollection<Point> = {
    type: "FeatureCollection",
    features: centroidFeatures
  };

  const sourceSpec: SourceSpecification = {
    type: "geojson",
    data: centroidCollection
  };

  if (map.getSource(centroidSourceId)) {
    const source = map.getSource<GeoJSONSource>(centroidSourceId);
    source?.setData(centroidCollection);
  } else {
    map.addSource(centroidSourceId, sourceSpec);
  }
};

export const addPolygonCatchmentSource = (
  map: MaplibreMap | null,
  datasetId: string,
  polygonData: FeatureCollection<Geometry> | Feature<Polygon, GeoJsonProperties>
): void => {
  if (!map) return;

  map.addSource(datasetId, {
    type: "geojson",
    data: polygonData
  });
};

export const addPolygonSource = (
  map: MaplibreMap | null,
  datasetId: string,
  polygonData: FeatureCollection<Geometry>
): void => {
  if (!map) return;

  const parsedPolygonData: FeatureCollection<Geometry> = {
    ...polygonData,
    features: polygonData.features.map((feature, index) => ({
      ...feature,
      properties: {
        ...feature.properties,
        unique_id: feature.properties?.unique_id || index
      }
    }))
  };

  map.addSource(datasetId, {
    type: "geojson",
    data: parsedPolygonData,
    promoteId: "unique_id"
  });
};

export const removePolygonSource = (
  map: MaplibreMap | null,
  datasetId: string
): void => {
  if (!map) return;

  map.removeSource(datasetId);
};

export const removePolygonLayer = (
  map: MaplibreMap | null,
  datasetId: string
): void => {
  if (!map) return;

  map.removeLayer(datasetId);
};

export const addPolygonOrdinalLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  layerData: Omit<FeatureDataConfig, "data"> & { id: string },
  style: Dataset["style"],
  category: string
): void => {
  if (!map || !layerData.sublayers) return;

  const colorRanges = style.colorRanges as { value: string; color: string }[];

  // Construct the match expression with unique, sorted values
  // const matchExpression =;

  // Add fill layer
  map.addLayer({
    id: layerData.sublayers.fill,
    type: "fill",
    source: layerData.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "fill-color": [
        "case",
        ["has", category],
        [
          "match",
          ["get", category],
          colorRanges[0]?.value ?? "",
          COLORS[style.colorFamily][COLOR_KEYS[0]],
          ...(colorRanges
            .slice(1)
            ?.flatMap((range, index) => [
              range.value ?? "",
              COLORS[style.colorFamily][COLOR_KEYS[index + 1]]
            ]) ?? []),
          COLORS[style.colorFamily][COLOR_KEYS[colorRanges.length - 1]]
        ],
        "#74c476"
      ],

      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.8,
        style?.fillOpacity || 0.7
      ]
    }
  });

  // Add outline layer
  map.addLayer({
    id: layerData.sublayers.outline,
    type: "line",
    source: layerData.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        "#FFCF01", // Visible outline when selected
        "#ffffff" // No outline when not selected
      ],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        2, // Wider outline when selected
        1 // No outline when not selected
      ]
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, layerData, style);
  addRankLabelLayer(isRankingLabelEnabled, map, layerData, style);
};

export const addPolygonQuantileLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataset: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map || !dataset.sublayers) return;

  const uniqueRanges = generateColorRangesUniqueStops(
    style.colorRanges,
    style.colorFamily,
    style.colorSteps || DEFAULT_STEP
  );

  // NEW: Build a valid `step` expression regardless of the length of `uniqueRanges`
  const toLiteralColorExpression = (color: string): ExpressionSpecification => [
    "literal",
    color
  ];

  const stepColorExpression: ExpressionSpecification = (() => {
    if (uniqueRanges.length >= 3) {
      // Exclude any ranges whose stop value is not a finite number (e.g., null)
      const numericStops = uniqueRanges
        .slice(1)
        .filter(([v]) => Number.isFinite(v))
        .flatMap(([v, c]) => [v, c]);

      return ["step", ["get", category], uniqueRanges[0][1], ...numericStops];
    }

    if (uniqueRanges.length === 2) {
      // Minimum valid form: default + one stop-pair (4 args total)
      return [
        "step",
        ["get", category],
        uniqueRanges[0][1], // default colour (first range)
        uniqueRanges[1][0], // stop value (second range min)
        uniqueRanges[1][1] // colour for values >= stop
      ];
    }

    if (uniqueRanges.length === 1) {
      // Single range - create a minimal step expression with the available data
      const [minValue, color] = uniqueRanges[0];
      if (Number.isFinite(minValue)) {
        // Use the color as default and create a step at the min value
        return [
          "step",
          ["get", category],
          color, // default color
          minValue, // step threshold
          color // same color for values >= threshold
        ];
      }
      // If minValue is not finite, return constant color
      return toLiteralColorExpression(color);
    }

    // Fallback – no data, use constant grey
    return toLiteralColorExpression("#CECECE");
  })();

  // Add fill layer
  map.addLayer({
    id: dataset.sublayers.fill,
    type: "fill",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "fill-color": [
        "case",
        /* 1️⃣ category is null  → grey */
        ["==", ["get", category], null],
        "#CECECE",

        /* 2️⃣ property exists and is not null → use your step scale or constant color */
        ["has", category],
        stepColorExpression,

        /* 3️⃣ property missing entirely → white */
        "#FFFFFF"
      ],
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.8,
        style?.fillOpacity || 0.7
      ],
      // Border color based on selected state
      "fill-outline-color": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        "#FFCF01", // border color when selected is true
        "#fff" // default border color when selected is false
      ]
    }
  });

  // Add outline layer
  map.addLayer({
    id: dataset.sublayers.outline,
    type: "line",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        "#FFCF01", // Visible outline when selected
        "#ffffff" // No outline when not selected
      ],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        2, // Wider outline when selected
        1 // No outline when not selected
      ]
    }
  });
  addLabelLayer(isNewUIDatasetConfigEnabled, map, dataset, style);
  addRankLabelLayer(isRankingLabelEnabled, map, dataset, style);
};
